import { Box, Stack } from "@mui/material";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation } from "react-router-dom";

import ConditionalFeatureRouteWrapper from "~/app/ConditionalFeatureRouteWrapper";
import { useAppSelector } from "~/app/store";
import useFlag from "~/config/flags";
import Andon from "~/features/andon/Andon";
import { selectEnableDevInformation } from "~/features/devTools/devTools.slice";
import { Status } from "~/features/status/Status";
import { TaskAssessment } from "~/features/status/TaskAssessment";
import TestingToolsView from "~/features/testingTools/TestingToolsView";
import { useDevCheats } from "~/hooks/useDevCheats";
import { useNavbar } from "~/hooks/useNavbar";
import { useWorkstationPolling } from "~/hooks/useWorkstationPolling";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectUserIsAdmin } from "~/redux/selectors/authSelectors";
import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";

import Configurations from "./Configurations";
import ServiceSupport from "./ServiceSupport";
import ServiceSupportView from "./ServiceSupportView";
import { UnifyView } from "./UnifyView";
import EventsTable from "./eventsMonitor/EventsTable";

export const SERVICE_SUPPORT_MAIN_TAB_QUERY_KEY = "service-support-tab";

function AdminServiceSupportGate({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  return (
    <ConditionalFeatureRouteWrapper
      isAllowed={!!isAdmin}
      altMessage={t("user must be an administrator")}
    >
      {children}
    </ConditionalFeatureRouteWrapper>
  );
}

function LegacyTabNavigate({ tab }: { tab: string }) {
  return (
    <Navigate
      to={{
        pathname: "/service-support",
        search: `?${SERVICE_SUPPORT_MAIN_TAB_QUERY_KEY}=${encodeURIComponent(tab)}`
      }}
      replace
    />
  );
}

function wrapServiceSupportSubnavSection(children: ReactNode) {
  return (
    <Stack
      margin={3}
      height="calc(100% - 48px)"
      sx={{
        overflowY: "unset"
      }}
    >
      <Box height="100%">{children}</Box>
    </Stack>
  );
}

function ServiceSupportHooksShell({ children }: { children: React.ReactNode }) {
  useDevCheats({ showEventSimulator: true });
  useWorkstationPolling();
  return <>{children}</>;
}

function ServiceSupportMainSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<ServiceSupport />);
}

export function ServiceSupportRootRoute() {
  return <ServiceSupportRootRouteInner />;
}

function ServiceSupportRootRouteInner() {
  const { t } = useTranslation();
  const isAdmin = useAppSelector(selectUserIsAdmin);
  const { enableSideNavSubMenus } = useFlag();
  return (
    <ConditionalFeatureRouteWrapper
      isAllowed={!!isAdmin}
      altMessage={t("user must be an administrator")}
    >
      {!enableSideNavSubMenus ? (
        <ServiceSupportView viewTitle="nav.viewname.service and support" />
      ) : (
        <ServiceSupportHooksShell>
          <ServiceSupportMainSection />
        </ServiceSupportHooksShell>
      )}
    </ConditionalFeatureRouteWrapper>
  );
}

export function useServiceSupportSectionGuards() {
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const { an_andonFeaturesEnabled } = useAppSelector(selectServerConfig);
  const shouldRenderTasksTab =
    usersFulfillmentCenter?.pickingConfigurations.includes("Autostore") ??
    false;
  const { enableTestingTools: enableTestingToolsTab } = useFlag();
  const shouldDisplayAndonTab = an_andonFeaturesEnabled;
  const shouldRenderConfigurationsTab = useAppSelector(
    selectEnableDevInformation
  );
  return {
    shouldRenderTasksTab,
    enableTestingToolsTab,
    shouldDisplayAndonTab,
    shouldRenderConfigurationsTab
  };
}

/**
 * Legacy: Cube Analytics lived under /service-support/unify.
 * TODO(cube-analytics-legacy): back-compat redirect only — remove alongside
 * legacyUnifyDeepLink in ServiceSupportView.tsx once old `unify` links are no
 * longer in use (a release or two after the rename ships).
 */
export function ServiceSupportUnifyLegacyPathRoute() {
  const { search } = useLocation();
  return (
    <Navigate
      to={{ pathname: "/service-support/cube-analytics", search }}
      replace
    />
  );
}

export function ServiceSupportUnifySectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportUnifySectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportUnifySectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="cube-analytics" />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportUnifySection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportUnifySection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<UnifyView />);
}

export function ServiceSupportStatusSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportStatusSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportStatusSectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="status" />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportStatusSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportStatusSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<Status />);
}

export function ServiceSupportTasksSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportTasksSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportTasksSectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  const { shouldRenderTasksTab } = useServiceSupportSectionGuards();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="tasks" />;
  }
  if (!shouldRenderTasksTab) {
    return <Navigate to="/service-support" replace />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportTasksSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportTasksSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<TaskAssessment />);
}

export function ServiceSupportAndonSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportAndonSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportAndonSectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  const { shouldDisplayAndonTab } = useServiceSupportSectionGuards();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="andon" />;
  }
  if (!shouldDisplayAndonTab) {
    return <Navigate to="/service-support" replace />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportAndonSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportAndonSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<Andon />);
}

export function ServiceSupportEventsSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportEventsSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportEventsSectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  const { shouldDisplayAndonTab } = useServiceSupportSectionGuards();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="events" />;
  }
  if (!shouldDisplayAndonTab) {
    return <Navigate to="/service-support" replace />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportEventsSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportEventsSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<EventsTable />);
}

export function ServiceSupportConfigurationsSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportConfigurationsSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportConfigurationsSectionRouteInner() {
  const { enableSideNavSubMenus } = useFlag();
  const { shouldRenderConfigurationsTab } = useServiceSupportSectionGuards();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="configurations" />;
  }
  if (!shouldRenderConfigurationsTab) {
    return <Navigate to="/service-support" replace />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportConfigurationsSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportConfigurationsSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<Configurations />);
}

export function ServiceSupportTestingToolsSectionRoute() {
  return (
    <AdminServiceSupportGate>
      <ServiceSupportTestingToolsSectionRouteInner />
    </AdminServiceSupportGate>
  );
}

function ServiceSupportTestingToolsSectionRouteInner() {
  const { enableSideNavSubMenus, enableTestingTools: enableTestingToolsTab } =
    useFlag();
  if (!enableSideNavSubMenus) {
    return <LegacyTabNavigate tab="testing-tools" />;
  }
  if (!enableTestingToolsTab) {
    return <Navigate to="/service-support" replace />;
  }
  return (
    <ServiceSupportHooksShell>
      <ServiceSupportTestingToolsSection />
    </ServiceSupportHooksShell>
  );
}

function ServiceSupportTestingToolsSection() {
  useNavbar({ viewTitle: "nav.viewname.service and support" });
  return wrapServiceSupportSubnavSection(<TestingToolsView />);
}
